import type { ExecuteProcessorMutation } from '#src/graphql/generated';
import type { ApolloClient } from '@apollo/client';

import { useApolloClient, useMutation } from '@apollo/client/react';

import { toaster } from '#src/components/ui/toaster';
import { EXECUTE_PROCESSOR } from '#src/graphql/mutations/execute-processor';
import { persistProcessorFailureAsGlobalRootMessage } from '#src/utils/persist-processor-failure-message';

async function handleExecuteProcessorCompleted(
  apolloClient: ApolloClient,
  data: ExecuteProcessorMutation,
  setOutputJson: (value: string | null) => void,
): Promise<void> {
  const res = data?.executeProcessor;
  if (!res) return;
  if (res.success && res.result !== undefined && res.result !== null) {
    setOutputJson(
      typeof res.result === 'string'
        ? res.result
        : JSON.stringify(res.result, null, 2),
    );
  } else {
    const errText = res.error ?? 'Unknown error';
    setOutputJson(JSON.stringify({ success: false, error: errText }, null, 2));
    const persist = await persistProcessorFailureAsGlobalRootMessage(
      apolloClient,
      {
        errorMessage: errText,
        processorInstanceId: res.instanceId,
      },
    );
    if (!persist.ok) {
      toaster.create({
        title: 'Could not save error to messages',
        description: persist.error,
        type: 'warning',
      });
    }
  }
}

async function handleExecuteProcessorError(
  apolloClient: ApolloClient,
  error: { message?: string },
  setOutputJson: (value: string | null) => void,
): Promise<void> {
  const errText = error.message ?? 'Network error';
  setOutputJson(JSON.stringify({ success: false, error: errText }, null, 2));
  const persist = await persistProcessorFailureAsGlobalRootMessage(
    apolloClient,
    {
      errorMessage: errText,
      processorInstanceId: undefined,
    },
  );
  if (!persist.ok) {
    toaster.create({
      title: 'Could not save error to messages',
      description: persist.error,
      type: 'warning',
    });
  }
}

export function useExecuteProcessorMutation(
  setOutputJson: (value: string | null) => void,
) {
  const apolloClient = useApolloClient();
  return useMutation(EXECUTE_PROCESSOR, {
    onCompleted: (data) => {
      void handleExecuteProcessorCompleted(apolloClient, data, setOutputJson);
    },
    onError: (error) => {
      void handleExecuteProcessorError(apolloClient, error, setOutputJson);
    },
  });
}
